import { z } from "zod";

import { BRAND_MARKUP } from "../../constants";
import { ValidationError } from "../../errors";
import { attachment } from "../shared";
import { defineModel } from "../types";

const VEO31_FAST_EXTEND_SECONDS = 7;
const VEO31_FAST_EXTEND_COST_PER_SECOND = 1_000_000 * 0.08 * (1 + BRAND_MARKUP);

const veo31FastExtendParams = z
  .object({
    aspectRatio: z.enum(["16:9", "9:16"]).default("9:16"),
    attachments: z.array(attachment).optional(),
    prompt: z.string().default(""),
  })
  .strip();

export const veo31FastExtend = defineModel({
  key: "veo3_1-fast-extend",
  stepType: "video",
  spec: {
    label: "Veo 3.1 Fast Extend",
    description: "Extend an existing Veo 3.1 video by continuing the scene",
    icon: "gemini-icon.svg",
    tags: ["video-extend"],
    featured: false,
    info: "Extends a previous Veo 3.1 generation by continuing from the final second of the source video.",
    features: {
      aspectRatio: true,
      attachments: true,
      prompts: true,
    } as const,
    options: {
      attachments: [
        {
          type: "video",
          allowedFormats: ["video/mp4"],
          label: "Source Video",
          slot: "source_video",
          required: true,
        },
      ],
      aspectRatio: {
        items: [
          { label: "9:16", value: "9:16" },
          { label: "16:9", value: "16:9" },
        ],
      },
    },
    params: veo31FastExtendParams,
  },
  referencePricing: {
    provider: "Vertex AI",
    url: "https://ai.google.dev/gemini-api/docs/video#extending",
    entries: [
      {
        unit: "per_second",
        unitPriceMicrodollars: 80_000,
        variant: "720p",
      },
    ],
  },
  providers: [
    {
      provider: "vertex",
      modelId: "veo-3.1-fast-generate-001",
      estimateCost() {
        return Math.ceil(
          VEO31_FAST_EXTEND_COST_PER_SECOND * VEO31_FAST_EXTEND_SECONDS,
        );
      },
      estimateCostPerSecond() {
        return VEO31_FAST_EXTEND_COST_PER_SECOND;
      },
      costPerSecond: VEO31_FAST_EXTEND_COST_PER_SECOND,
      toPayload(params) {
        const sourceVideo = params.attachments?.find(
          (attachment) => attachment.slot === "source_video",
        )?.data;

        if (!sourceVideo) {
          throw new ValidationError(
            "Veo extension requires a source video attachment.",
          );
        }

        return {
          model: this.modelId,
          prompt: params.prompt,
          video: { url: sourceVideo },
          config: {
            aspectRatio: params.aspectRatio,
            durationSeconds: VEO31_FAST_EXTEND_SECONDS,
            resolution: "720p",
          },
        };
      },
    },
  ],
});
